import { useEffect } from "react";
import { motion } from "framer-motion";
import { Eye, FileInput, MessagesSquare, Mail, Phone, CalendarCheck, CreditCard, ShieldCheck, RefreshCw } from "lucide-react";
import { RevShell, ModuleBar, RevHero, SectionHead, RevCTA, BrowserFrame, ComplianceNote } from "./_shared";

const CRMS = ["HubSpot", "Salesforce", "Zoho", "Pipedrive", "Odoo"];

const TIMELINE = [
  { icon: Eye, t: "Viewed Pricing page", meta: "10:02 · from Google", tone: "slate" },
  { icon: FileInput, t: "Submitted contact form", meta: "10:05 · Lead score 68", tone: "brand" },
  { icon: MessagesSquare, t: "Chatted with Ava — qualified", meta: "10:06 · budget + timing captured", tone: "brand" },
  { icon: CalendarCheck, t: "Booked appointment", meta: "10:08 · Fri 4:15pm", tone: "emerald" },
  { icon: Mail, t: "Reminder email sent", meta: "Thu 09:00 · opened", tone: "slate" },
  { icon: Phone, t: "Attended call", meta: "Fri 16:15 · summary logged", tone: "brand" },
  { icon: CreditCard, t: "Paid deposit", meta: "Fri 16:40 · $250", tone: "emerald" },
];

const T_TONE = { slate: "text-slate-500 bg-slate-100", brand: "text-brand bg-blue-50", emerald: "text-emerald-600 bg-emerald-50" };

const SYNC = [
  "Bidirectional field mapping", "Deduplication", "Configurable triggers", "Activity synchronization", "Consent & opt-out history", "Source & campaign attribution",
];

function TimelineMock() {
  return (
    <BrowserFrame url="app.websitetech.pro/timeline/contact-4821">
      <div className="flex items-center justify-between mb-3">
        <div className="text-sm font-bold text-slate-800">Priya N.</div>
        <span className="text-[10px] font-semibold text-emerald-600 bg-emerald-50 rounded-full px-2 py-0.5">Won · $250</span>
      </div>
      <div className="space-y-0">
        {TIMELINE.map((e, i) => (
          <div key={i} className="flex gap-3">
            <div className="flex flex-col items-center">
              <div className={`h-7 w-7 rounded-lg grid place-items-center shrink-0 ${T_TONE[e.tone]}`}><e.icon size={13} /></div>
              {i < TIMELINE.length - 1 && <div className="w-px flex-1 bg-slate-200 my-1" />}
            </div>
            <div className="pb-3">
              <div className="text-[13px] font-semibold text-slate-700 leading-tight">{e.t}</div>
              <div className="text-[10px] text-slate-400">{e.meta}</div>
            </div>
          </div>
        ))}
      </div>
    </BrowserFrame>
  );
}

export default function Timeline() {
  useEffect(() => { window.scrollTo(0, 0); document.title = "Unified Timeline & CRM Sync | AI Revenue Center"; }, []);
  return (
    <RevShell testid="rev-timeline-page">
      <RevHero eyebrow="AI Revenue Center™ · Customer Timeline & CRM"
        title="One record for every customer —" accent="not ten disconnected tools."
        sub="Pages viewed, forms, chats, calls, emails, appointments, payments, consent and lead score — in a single chronological timeline that syncs both ways with your CRM."
        visual={<TimelineMock />} />
      <ModuleBar />

      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <SectionHead eyebrow="Everything in one place" title="The full story of every relationship" sub="No more piecing together what happened across email, phone notes and three dashboards." />
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mt-8">
          {["Pages viewed", "Forms submitted", "Chat conversations", "Emails & replies", "Calls & recordings", "SMS & WhatsApp", "Appointments", "Proposals", "Payments", "Support requests", "Consent & opt-outs", "Lead score & stage"].map((x) => (
            <div key={x} className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">{x}</div>
          ))}
        </div>
      </section>

      <section className="py-16 bg-white border-y border-slate-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHead eyebrow="CRM sync" title="Works with the CRM you already use" />
          <div className="flex flex-wrap gap-2.5 mt-8">
            {CRMS.map((c) => (
              <span key={c} className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-bold text-slate-700 card-shadow"><RefreshCw size={14} className="text-brand" /> {c}</span>
            ))}
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-8">
            {SYNC.map((s, i) => (
              <motion.div key={s} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.04 }}
                className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700"><ShieldCheck size={15} className="text-brand shrink-0" /> {s}</motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <ComplianceNote>
          Consent and opt-out history lives on the timeline itself — so anyone acting on a record can see exactly what that person has agreed to before reaching out.
        </ComplianceNote>
      </section>

      <RevCTA title="See every customer's full story." sub="Start with a free audit — we'll show you what your current tools are failing to connect." />
    </RevShell>
  );
}
